import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  MapPin, Calendar, Users, Vote, Plus, ChevronRight, 
  Target, Trophy, Loader2, Check, X, ChevronDown, Award, Building2, MessageSquare, Fish, Link2
} from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { getImageUrl } from "@/utils/imageUtils";
import { createEntityLink } from "@/components/EntityEmbed";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const Community = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [trips, setTrips] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTrip, setShowNewTrip] = useState(false);
  const [submittingTrip, setSubmittingTrip] = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);
  const [addingDateTo, setAddingDateTo] = useState(null);
  const [newDateInput, setNewDateInput] = useState("");
  const [highlightedTripId, setHighlightedTripId] = useState(null);
  const tripRefs = useRef({});
  const [newTrip, setNewTrip] = useState({
    title: "",
    description: "",
    location: "",
    country: "",
    proposed_dates: "",
    min_participants: 5
  });

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "null");
    setUser(currentUser);
    
    // Redirect dive_center users to pros page
    if (currentUser?.role === "dive_center") {
      navigate("/community/pros");
      return;
    }
    
    fetchData();
  }, [navigate]);

  // Scroll to specific trip when linked via ?trip=id
  useEffect(() => {
    const tripId = searchParams.get("trip");
    if (tripId && !loading && trips.length > 0) {
      setHighlightedTripId(tripId);
      setTimeout(() => {
        const el = tripRefs.current[tripId];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedTripId(null), 4000);
    }
  }, [searchParams, loading, trips]);

  const fetchData = async () => {
    try {
      const [tripsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/trips`),
        axios.get(`${API}/community/projects?status=active`)
      ]);
      setTrips(tripsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error("Error fetching community data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (submittingTrip) return;

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      toast.error("Please log in to create a trip proposal");
      return;
    }

    // Basic validation — surface what's missing instead of failing silently.
    if (!newTrip.title?.trim() || !newTrip.location?.trim() || !newTrip.country?.trim()) {
      toast.error("Title, location and country are required");
      return;
    }

    setSubmittingTrip(true);
    try {
      const dates = (newTrip.proposed_dates || "")
        .split(",")
        .map(d => d.trim())
        .filter(d => d);

      await axios.post(`${API}/trips`, {
        ...newTrip,
        proposed_dates: dates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Trip proposal created!");
      setShowNewTrip(false);
      setNewTrip({
        title: "",
        description: "",
        location: "",
        country: "",
        proposed_dates: "",
        min_participants: 5
      });
      fetchData();
    } catch (error) {
      console.error("Create trip failed:", error);
      toast.error(error.response?.data?.detail || "Failed to create trip");
    } finally {
      setSubmittingTrip(false);
    }
  };

  const handleVote = async (tripId, dateToToggle) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      toast.error("Please log in to vote");
      return;
    }

    // Get current user's votes for this trip
    const trip = trips.find(t => t.id === tripId);
    const userVote = trip?.votes?.find(v => v.user_id === user?.id);
    const currentDates = userVote?.preferred_dates || [];
    
    // Toggle the date - add if not present, remove if present
    let newDates;
    if (currentDates.includes(dateToToggle)) {
      newDates = currentDates.filter(d => d !== dateToToggle);
    } else {
      newDates = [...currentDates, dateToToggle];
    }

    try {
      await axios.post(`${API}/trips/${tripId}/vote`, {
        trip_id: tripId,
        preferred_dates: newDates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(currentDates.includes(dateToToggle) ? "Vote removed" : "Vote added!");
      fetchData();
      // Trigger chat room refresh so trip chat appears/disappears
      window.dispatchEvent(new CustomEvent('refreshChatRooms'));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to vote");
    }
  };

  // Get the maximum votes on any single date for a trip
  const getMaxVoteCount = (trip) => {
    if (!trip.proposed_dates?.length) return 0;
    let maxVotes = 0;
    for (const date of trip.proposed_dates) {
      const voters = getVotersForDate(trip, date);
      if (voters.length > maxVotes) {
        maxVotes = voters.length;
      }
    }
    return maxVotes;
  };

  const handleSuggestDate = async (tripId) => {
    if (!newDateInput.trim()) {
      toast.error("Please enter a date");
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      toast.error("Please log in");
      return;
    }

    try {
      await axios.post(`${API}/trips/${tripId}/suggest-date?date=${encodeURIComponent(newDateInput)}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Date suggested!");
      setAddingDateTo(null);
      setNewDateInput("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suggest date");
    }
  };

  const getVotersForDate = (trip, date) => {
    return trip.votes?.filter(v => v.preferred_dates?.includes(date)) || [];
  };

  const hasVoted = (trip) => {
    if (!user) return false;
    return trip.votes?.some(v => v.user_id === user.id);
  };

  const hasVotedForDate = (trip, date) => {
    if (!user) return false;
    const userVote = trip.votes?.find(v => v.user_id === user.id);
    return userVote?.preferred_dates?.includes(date);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "voting": return "bg-blue-100 text-blue-700";
      case "confirmed": return "bg-green-100 text-green-700";
      case "cancelled": return "bg-red-100 text-red-700";
      case "completed": return "bg-gray-100 text-gray-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen">
        <SEO title="Community - George Dives" description="Join the George Dives community" />
        <Navigation />
        <FloatingUserMenu />
        
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto text-center py-20">
              <Users className="w-16 h-16 text-cyan-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-4">Members Only</h1>
              <p className="text-gray-600 mb-6">
                The community area is exclusive to George Dives members. Please log in to access trip voting, community projects, and more.
              </p>
              <Button onClick={() => navigate("/account")} className="bg-cyan-500 hover:bg-cyan-600">
                Log In
              </Button>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <SEO title="Community - George Dives" description="Join the George Dives community" />
      <Navigation />
      <FloatingUserMenu />
      
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Community Hub</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Vote for upcoming trips, track community goals, and connect with fellow divers.
            </p>
            
            {/* Quick Links to Pros/Dive Centers */}
            {user && (
              <div className="flex justify-center gap-4 mt-6 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => navigate("/community/marine-database")}
                  className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  data-testid="community-marine-db-btn"
                >
                  <Fish className="w-4 h-4 mr-2" />
                  Marine Database
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/community/forum")}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="community-forum-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Forum
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/community/pros")}
                  className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Browse Pros
                </Button>
                {(user.role === "pro" || user.role === "admin") && (
                  <Button
                    variant="outline"
                    onClick={() => navigate("/community/dive-centers")}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Recruiting Dive Centers
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Trip Voting Section */}
            <div className="lg:col-span-2">
              <Card data-testid="trip-voting-section">
                <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <Vote className="w-5 h-5 text-cyan-600" />
                    Upcoming Trip Voting
                  </CardTitle>
                  {user && (
                    <Button 
                      onClick={() => setShowNewTrip(!showNewTrip)} 
                      size="sm"
                      variant={showNewTrip ? "outline" : "default"}
                    >
                      {showNewTrip ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                      {showNewTrip ? "Cancel" : "Propose Trip"}
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {/* New Trip Form */}
                  {showNewTrip && (
                    <form onSubmit={handleCreateTrip} className="mb-6 p-4 bg-cyan-50 rounded-lg space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Trip Title</Label>
                          <Input
                            value={newTrip.title}
                            onChange={(e) => setNewTrip({...newTrip, title: e.target.value})}
                            placeholder="e.g., Tubbataha Reef Adventure"
                            required
                          />
                        </div>
                        <div>
                          <Label>Location</Label>
                          <Input
                            value={newTrip.location}
                            onChange={(e) => setNewTrip({...newTrip, location: e.target.value})}
                            placeholder="e.g., Tubbataha Reef"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Country</Label>
                          <Input
                            value={newTrip.country}
                            onChange={(e) => setNewTrip({...newTrip, country: e.target.value})}
                            placeholder="e.g., Philippines"
                            required
                          />
                        </div>
                        <div>
                          <Label>Min. Participants</Label>
                          <Input
                            type="number"
                            min="2"
                            value={newTrip.min_participants}
                            onChange={(e) => setNewTrip({...newTrip, min_participants: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Proposed Dates (comma separated)</Label>
                        <Input
                          value={newTrip.proposed_dates}
                          onChange={(e) => setNewTrip({...newTrip, proposed_dates: e.target.value})}
                          placeholder="e.g., March 15-20, March 22-27, April 5-10"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <textarea
                          value={newTrip.description}
                          onChange={(e) => setNewTrip({...newTrip, description: e.target.value})}
                          placeholder="Tell us about this trip..."
                          className="w-full px-3 py-2 border rounded-md min-h-[80px]"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={submittingTrip}
                        data-testid="submit-trip-proposal-btn"
                        className="bg-cyan-500 hover:bg-cyan-600 disabled:opacity-60"
                      >
                        {submittingTrip ? "Submitting..." : "Submit Proposal"}
                      </Button>
                    </form>
                  )}

                  {/* Trips List */}
                  {loading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
                    </div>
                  ) : trips.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      No trip proposals yet. Be the first to propose one!
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {trips.map((trip) => (
                        <div
                          key={trip.id}
                          ref={el => { tripRefs.current[trip.id] = el; }}
                          data-testid={`trip-card-${trip.id}`}
                          className={`border rounded-lg p-4 transition-all duration-500 ${
                            highlightedTripId === trip.id
                              ? "ring-2 ring-cyan-400 bg-cyan-50/50"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-lg">{trip.title}</h3>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(trip.status)}`}>
                                  {trip.status}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const link = createEntityLink("trip", trip.id);
                                    navigator.clipboard.writeText(link);
                                    toast.success("Trip link copied!");
                                  }}
                                  className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                  data-testid={`copy-trip-link-${trip.id}`}
                                  title="Copy trip link"
                                >
                                  <Link2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {trip.location}, {trip.country}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {getMaxVoteCount(trip)} / {trip.min_participants} votes
                                </span>
                                {trip.chat_message_count > 0 && hasVoted(trip) && (
                                  <span className="flex items-center gap-1 text-emerald-600" data-testid={`trip-chat-badge-${trip.id}`}>
                                    <MessageSquare className="w-4 h-4" />
                                    {trip.chat_message_count}
                                  </span>
                                )}
                              </div>
                              {trip.description && (
                                <p className="text-sm text-gray-600 mb-3">{trip.description}</p>
                              )}
                              
                              {/* Date Options */}
                              {trip.proposed_dates?.length > 0 && trip.status === "voting" && (
                                <div className="mt-3">
                                  <p className="text-xs text-gray-500 mb-2">Select dates that work for you:</p>
                                  <div className="space-y-2">
                                    {trip.proposed_dates.map((date) => {
                                      const voters = getVotersForDate(trip, date);
                                      const voteCount = voters.length;
                                      const userVoted = hasVotedForDate(trip, date);
                                      const isExpanded = expandedDate === `${trip.id}-${date}`;
                                      
                                      return (
                                        <div key={date} className="flex flex-col">
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => handleVote(trip.id, date)}
                                              className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-colors ${
                                                userVoted 
                                                  ? "bg-cyan-500 text-white" 
                                                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                              }`}
                                            >
                                              <Calendar className="w-3 h-3" />
                                              {date}
                                              <span className="ml-1 text-xs opacity-75">({voteCount})</span>
                                            </button>
                                            
                                            {/* Voter Avatars */}
                                            {voters.length > 0 && (
                                              <div className="relative">
                                                <button
                                                  onClick={() => setExpandedDate(isExpanded ? null : `${trip.id}-${date}`)}
                                                  className="flex -space-x-2 hover:opacity-80"
                                                >
                                                  {voters.slice(0, 5).map((voter, idx) => (
                                                    voter.user_profile_pic ? (
                                                      <img
                                                        key={voter.user_id || idx}
                                                        src={getImageUrl(voter.user_profile_pic)}
                                                        alt={voter.user_name}
                                                        className="w-6 h-6 rounded-full border-2 border-white object-cover"
                                                        title={voter.user_name}
                                                      />
                                                    ) : (
                                                      <div
                                                        key={voter.user_id || idx}
                                                        className="w-6 h-6 rounded-full bg-cyan-100 border-2 border-white flex items-center justify-center text-cyan-700 font-medium text-xs"
                                                        title={voter.user_name}
                                                      >
                                                        {voter.user_name?.charAt(0) || "?"}
                                                      </div>
                                                    )
                                                  ))}
                                                  {voters.length > 5 && (
                                                    <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-gray-600 font-medium text-xs">
                                                      +{voters.length - 5}
                                                    </div>
                                                  )}
                                                  <ChevronDown className={`w-4 h-4 text-gray-400 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                </button>
                                                
                                                {/* Voter Dropdown */}
                                                {isExpanded && (
                                                  <div className="absolute top-8 left-0 z-10 bg-white border rounded-lg shadow-lg p-2 min-w-[150px]">
                                                    <p className="text-xs text-gray-500 mb-1 px-2">Voters:</p>
                                                    {voters.map((voter, idx) => (
                                                      <div 
                                                        key={voter.user_id || idx} 
                                                        className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                                                        onClick={() => voter.user_id && navigate(`/profile/${voter.user_id}`)}
                                                        title={voter.user_id ? `View ${voter.user_name}'s profile` : ''}
                                                      >
                                                        {voter.user_profile_pic ? (
                                                          <img
                                                            src={getImageUrl(voter.user_profile_pic)}
                                                            alt={voter.user_name}
                                                            className="w-5 h-5 rounded-full object-cover"
                                                          />
                                                        ) : (
                                                          <div className="w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-medium text-xs">
                                                            {voter.user_name?.charAt(0) || "?"}
                                                          </div>
                                                        )}
                                                        <span className="text-sm text-gray-700 hover:text-cyan-600">{voter.user_name}</span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  
                                  {/* Suggest New Date */}
                                  <div className="mt-3 pt-3 border-t border-gray-100">
                                    {addingDateTo === trip.id ? (
                                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        <Input
                                          value={newDateInput}
                                          onChange={(e) => setNewDateInput(e.target.value)}
                                          placeholder="e.g., April 15-20"
                                          className="h-8 text-sm flex-1"
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleSuggestDate(trip.id);
                                            }
                                          }}
                                        />
                                        <Button 
                                          type="button"
                                          size="sm" 
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleSuggestDate(trip.id);
                                          }}
                                          className="h-8 bg-cyan-500 hover:bg-cyan-600"
                                        >
                                          Add
                                        </Button>
                                        <Button 
                                          type="button"
                                          size="sm" 
                                          variant="ghost"
                                          onClick={(e) => { 
                                            e.stopPropagation();
                                            setAddingDateTo(null); 
                                            setNewDateInput(""); 
                                          }}
                                          className="h-8"
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAddingDateTo(trip.id);
                                        }}
                                        className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                                      >
                                        <Plus className="w-3 h-3" />
                                        Suggest different date
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {hasVoted(trip) && (
                              <div className="flex items-center gap-1 text-green-600 text-sm">
                                <Check className="w-4 h-4" />
                                Voted
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Community Goals */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-cyan-600" />
                    Community Goals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {projects.length === 0 ? (
                    <p className="text-sm text-gray-500">No active projects</p>
                  ) : (
                    <div className="space-y-4">
                      {projects.map((project) => (
                        <div key={project.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm">{project.title}</h4>
                            {project.target_value && (
                              <span className="text-xs text-gray-500">
                                {Math.round((project.current_value / project.target_value) * 100)}%
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600">{project.description}</p>
                          {project.target_value && (
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-cyan-500 h-2 rounded-full transition-all"
                                style={{ width: `${Math.min((project.current_value / project.target_value) * 100, 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Your Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Trips Voted</span>
                      <span className="font-semibold">
                        {trips.filter(t => hasVoted(t)).length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active Trips</span>
                      <span className="font-semibold">
                        {trips.filter(t => t.status === "voting").length}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Community;
